/**
 * Diseases — Platform Admin страница справочника болезней (AK-232).
 *
 * Тонкий wrapper над `ReferenceCatalogList` + `DiseaseEditorDrawer`,
 * переиспользует reference-CRUD эталон AK-231 (культуры). Delete =
 * архивирование (soft-delete), как у культур.
 *
 * Дизайн: docs/superpowers/prototypes/ak226-reference-hub/index.html (B/C).
 */
import { useCallback, useMemo, useState } from 'react';
import { Archive, GitMerge, MoreHorizontal, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PlatformAdminLayout } from '@/layouts/PlatformAdminLayout';
import {
  ReferenceCatalogList,
  type ReferenceColumn,
} from '@/components/reference/ReferenceCatalogList';
import {
  ReferenceMergeDialog,
  type MergeCandidate,
} from '@/components/reference/ReferenceMergeDialog';
import { DiseaseEditorDrawer } from './DiseaseEditorDrawer';
import {
  useArchiveDisease,
  useCreateDisease,
  useDiseaseCategories,
  useDiseasesInfinite,
  useMergeDiseases,
  useUpdateDisease,
} from './hooks/useCatalogQueries';
import type {
  DiseaseListItem,
  DiseasePayload,
  ReferenceListQuery,
} from './types';
import { toast } from '@/lib/toast';

function plural(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return 'записей';
  if (m10 === 1) return 'запись';
  if (m10 >= 2 && m10 <= 4) return 'записи';
  return 'записей';
}

export function PlatformAdminDiseasesPage() {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<DiseaseListItem | null>(null);

  const categoriesQ = useDiseaseCategories();
  const categories = categoriesQ.data?.items ?? [];

  const query: ReferenceListQuery = useMemo(
    () => ({
      search: search.trim() || undefined,
      // backend filters by legacy text `category` (= category code).
      category:
        categoryFilter === 'all'
          ? undefined
          : categories.find((c) => c.id === categoryFilter)?.code,
    }),
    [search, categoryFilter, categories],
  );

  const diseasesQ = useDiseasesInfinite(query);
  const createMut = useCreateDisease();
  const updateMut = useUpdateDisease();
  const archiveMut = useArchiveDisease();
  const mergeMut = useMergeDiseases();

  const items = diseasesQ.data?.items ?? [];

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState<DiseaseListItem | null>(null);
  const total = diseasesQ.data?.total ?? 0;

  const handleCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const handleEdit = (d: DiseaseListItem) => {
    setEditing(d);
    setDrawerOpen(true);
  };

  const handleSubmit = async (payload: DiseasePayload) => {
    if (editing) {
      await updateMut.mutateAsync({ id: editing.id, payload });
      toast.success(`Болезнь «${payload.name_translations.ru}» сохранена`);
    } else {
      await createMut.mutateAsync(payload);
      toast.success(`Болезнь «${payload.name_translations.ru}» создана`);
    }
  };

  const pickerTargetCandidate = useMemo<MergeCandidate | null>(() => {
    if (!pickerTarget) return null;
    return {
      id: pickerTarget.id,
      label: pickerTarget.name_translations.ru ?? pickerTarget.latin_name ?? '—',
      sublabel: pickerTarget.latin_name ?? undefined,
    };
  }, [pickerTarget]);

  const pickerCandidates = useMemo<MergeCandidate[]>(
    () =>
      items
        .filter((d) => d.id !== pickerTarget?.id)
        .map((d) => ({
          id: d.id,
          label: d.name_translations.ru ?? d.latin_name ?? '—',
          sublabel: d.latin_name ?? undefined,
        })),
    [items, pickerTarget?.id],
  );

  const handlePickerPreview = useCallback(
    async (sourceId: string, targetId: string) => {
      const res = await mergeMut.mutateAsync({ sourceId, targetId, dryRun: true });
      return { totalRepointed: res.total_repointed, totalDeleted: res.total_deleted };
    },
    [mergeMut.mutateAsync],
  );

  const handlePickerConfirm = useCallback(
    async (sourceId: string, targetId: string) => {
      const label = pickerTarget?.name_translations.ru ?? 'эталон';
      try {
        await mergeMut.mutateAsync({ sourceId, targetId, dryRun: false });
        toast.success(`Поглощено в «${label}»`);
      } catch (err) {
        toast.error((err as Error).message);
        throw err;
      }
    },
    [mergeMut.mutateAsync, pickerTarget],
  );

  const handleArchive = async (d: DiseaseListItem) => {
    if (!window.confirm(`Архивировать «${d.name_translations.ru}»?`)) return;
    try {
      await archiveMut.mutateAsync(d.id);
      toast.success('Архивировано.');
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const columns: ReferenceColumn<DiseaseListItem>[] = [
    {
      key: 'name',
      header: 'Название (RU)',
      render: (d) => (
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => handleEdit(d)}
            className="text-left font-medium text-foreground hover:text-primary"
          >
            {d.name_translations.ru ?? d.latin_name ?? '—'}
          </button>
        </div>
      ),
    },
    {
      key: 'latin',
      header: 'Латинское',
      width: '200px',
      render: (d) => (
        <span className="text-sm italic text-muted-foreground">
          {d.latin_name ?? '—'}
        </span>
      ),
    },
    {
      key: 'category',
      header: 'Категория',
      width: '170px',
      render: (d) =>
        d.category_name ? (
          <Badge variant="secondary">{d.category_name}</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: 'parts',
      header: 'Поражаемые части',
      width: '180px',
      render: (d) => (
        <span className="text-sm text-muted-foreground">
          {d.affected_parts.length ? d.affected_parts.join(', ') : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      align: 'right',
      render: (d) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label={`Действия для ${d.name_translations.ru}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onSelect={() => handleEdit(d)}>
              <Pencil className="h-4 w-4" />
              Редактировать
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => { setPickerTarget(d); setPickerOpen(true); }}>
              <GitMerge className="h-4 w-4" />
              Поглотить
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => void handleArchive(d)}>
              <Archive className="h-4 w-4" />
              Архивировать
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <PlatformAdminLayout>
      <ReferenceCatalogList<DiseaseListItem>
        title="Болезни"
        subtitle={`Системный справочник · read-only baseline для всех хозяйств · ${total} ${plural(total)}`}
        createLabel="Создать болезнь"
        onCreate={handleCreate}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Поиск по названию или латыни…"
        items={items}
        isLoading={diseasesQ.isLoading}
        isError={diseasesQ.isError}
        error={diseasesQ.error ?? undefined}
        onRetry={() => diseasesQ.refetch()}
        getRowKey={(d) => d.id}
        total={total}
        unitLabel="болезней"
        hasMore={!!diseasesQ.hasNextPage}
        loadingMore={diseasesQ.isFetchingNextPage}
        onLoadMore={() => diseasesQ.fetchNextPage()}
        columns={columns}
        emptyTitle="Пока нет ни одной болезни"
        emptyHint="Создайте первую запись справочника болезней — название, категория возбудителя и характеристики станут доступны всем хозяйствам."
        toolbarSlot={
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все категории</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name_translations.ru ?? c.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <DiseaseEditorDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        mode={editing ? 'edit' : 'create'}
        disease={editing}
        categories={categories}
        onSubmit={handleSubmit}
      />

      <ReferenceMergeDialog
        mode="picker"
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        target={pickerTargetCandidate}
        candidates={pickerCandidates}
        entityAccusative="болезнь"
        onPreview={handlePickerPreview}
        onConfirm={handlePickerConfirm}
      />
    </PlatformAdminLayout>
  );
}

export default PlatformAdminDiseasesPage;
